/* eslint-disable react/prop-types */
import React from 'react';

/**
 * The stroke icons the "Table view" / "Cards view" designs inline (24×24 viewBox), copied as small
 * components so the dev panels add no icon library.
 */

const COLOR_TEXT = '#20242A';
const COLOR_SECONDARY = '#4B575B';
const COLOR_GREEN = '#008F4B';
const COLOR_AMBER = '#A85700';
const COLOR_WHITE = '#fff';

const Svg = ({ size = 16, stroke = COLOR_TEXT, strokeWidth = 1.8, style, children }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0, ...style }}
        aria-hidden="true"
    >
        {children}
    </svg>
);

export const IconPhoto = ({ size = 14 }) => (
    <Svg size={size} stroke={COLOR_WHITE}>
        <rect x="3" y="5" width="18" height="15" rx="2" />
        <path d="m3 15 5-4 4 3 4-4 5 5" />
    </Svg>
);

export const IconSize = ({ stroke = COLOR_TEXT }) => (
    <Svg stroke={stroke}>
        <path d="M3 8V3h5M21 8V3h-5M3 16v5h5M21 16v5h-5" />
    </Svg>
);

export const IconSleeps = ({ stroke = COLOR_TEXT }) => (
    <Svg stroke={stroke}>
        <path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3" />
        <path d="M22 20v-2a4 4 0 0 0-3-3.8" />
        <path d="M16 4.2a4 4 0 0 1 0 5.6" />
    </Svg>
);

export const IconBed = ({ size = 16, stroke = COLOR_TEXT, strokeWidth = 1.8 }) => (
    <Svg size={size} stroke={stroke} strokeWidth={strokeWidth}>
        <path d="M2 8v11M2 13h20v6M22 19v-6a3 3 0 0 0-3-3h-8v3" />
        <circle cx="7" cy="10" r="2" />
    </Svg>
);

export const IconView = ({ stroke = COLOR_TEXT }) => (
    <Svg stroke={stroke}>
        <path d="M3 21h18M6 21V10l4-3 4 3v11M14 21V13l4-2.5V21" />
        <path d="M8.5 12.5h1M8.5 16h1" />
    </Svg>
);

export const IconFreeCancellation = ({ size = 18 }) => (
    <Svg size={size} stroke={COLOR_GREEN}>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 4v4h4" />
    </Svg>
);

export const IconNonRefundable = ({ size = 18 }) => (
    <Svg size={size} stroke={COLOR_SECONDARY}>
        <circle cx="12" cy="12" r="9" />
        <path d="m9 9 6 6M15 9l-6 6" />
    </Svg>
);

export const IconMeal = ({ size = 18 }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <path d="M17 8h1a3 3 0 0 1 0 6h-1" />
        <path d="M3 8h14v5a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8Z" />
        <path d="M6 2v3M10 2v3M14 2v3" />
    </Svg>
);

export const IconRoomOnly = ({ size = 18 }) => <IconBed size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7} />;

export const IconInPolicy = () => (
    <Svg stroke={COLOR_GREEN}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
    </Svg>
);

export const IconOutOfPolicy = () => (
    <Svg stroke={COLOR_AMBER}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4.5M12 16h.01" />
    </Svg>
);

export const IconBack = () => (
    <Svg size={24} stroke={COLOR_TEXT} strokeWidth={2}>
        <path d="m15 18-6-6 6-6" />
    </Svg>
);

const IconAirConditioning = ({ size }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <path d="M12 2v20M4.9 6.5l14.2 11M19.1 6.5 4.9 17.5M2 12h20" />
    </Svg>
);

const IconWifi = ({ size }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <path d="M5 12.5a10 10 0 0 1 14 0M2 9a15 15 0 0 1 20 0M8.5 16a5 5 0 0 1 7 0" />
        <path d="M12 20h.01" />
    </Svg>
);

const IconTv = ({ size }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <rect x="2" y="6" width="20" height="13" rx="2" />
        <path d="m8 3 4 3 4-3" />
    </Svg>
);

const IconRoomService = ({ size }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <path d="M3 17h18M4.5 17a7.5 7.5 0 0 1 15 0M12 6.5V9" />
        <circle cx="12" cy="5" r="1.2" />
    </Svg>
);

const IconShower = ({ size }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <path d="M12 3s5 5.5 5 9a5 5 0 0 1-10 0c0-3.5 5-9 5-9Z" />
    </Svg>
);

const IconPerkDefault = ({ size }) => (
    <Svg size={size} stroke={COLOR_SECONDARY} strokeWidth={1.7}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
    </Svg>
);

// The design draws five specific perk glyphs; a perk string that matches none of them gets the
// generic check so every chip still carries an icon.
const PERK_ICONS = [
    [/air.?condition|climate/i, IconAirConditioning],
    [/wi-?fi|internet/i, IconWifi],
    [/\btv\b|television/i, IconTv],
    [/room service|minibar|mini-bar|coffee|tea/i, IconRoomService],
    [/shower|bath|toilet|towel|toiletr/i, IconShower],
];

export const PerkIcon = ({ name, size = 16 }) => {
    const hit = PERK_ICONS.find(([pattern]) => pattern.test(String(name || '')));
    const Icon = hit ? hit[1] : IconPerkDefault;
    return <Icon size={size} />;
};
